import { useEffect, useMemo, useState, Fragment } from "react";
import axios from "../../util/axiosInstance";
import { toast } from "react-toastify";
import { Dialog, Transition } from "@headlessui/react";
import { getUIDescription } from "../../../components/SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";
// import AssignSubsDialog from "./assignSubsDialog";
// import ConfirmDialog from "../ConfirmDialog";

interface Unit {
  id: string;
  name: string;
  [key: string]: any;
}
interface Subscriber {
  id: string;
  user: {
    name: string;
    email: string;
  };
  daily: boolean;
  weekly: boolean;
  monthly: boolean;
}

interface ReportGroup {
  id: string;
  name: string;
}

interface ReportGroupSubsProps {
  group: ReportGroup | null;
}

interface AssignSubsDialogProps {
  open: boolean;
  onClose: () => void;
  groupId?: string;
  groupName?: string;
  onAssigned?: () => void;
}

export default function ReportGroupSubs({ group }: ReportGroupSubsProps) {
  const [loading, setLoading] = useState(false);
  const [groupSubs, setGroupSubs] = useState<Subscriber[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<keyof Subscriber["user"]>("name");
  const [searchQuery, setSearchQuery] = useState("");
  const [assignSubsDialogOpen, setAssignSubsDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [subsToDelete, setSubsToDelete] = useState<string[]>([]);

  const fetchSubs = async () => {
    if (!group?.id) return;
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/subscribers?groupId=${group.id}`
      );
      setGroupSubs(response.data || []);
    } catch (err) {
      console.error("Error fetching subscribers:", err);
      setError("Failed to load subscribers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubs();
  }, [group?.id]);

  const handleRequestSort = (property: keyof Subscriber["user"]) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedSubsData = useMemo(() => {
    return [...groupSubs].sort((a, b) => {
      let aValue = a.user[orderBy];
      let bValue = b.user[orderBy];

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = (bValue as string).toLowerCase();
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [groupSubs, order, orderBy]);

  const filteredSubsData = sortedSubsData.filter(
    (row) =>
      row.user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDeleteClick = (subId: string) => {
    setSubsToDelete([subId]);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!subsToDelete.length) return;
    try {
      setConfirmOpen(false);
      const response = await axios.delete(
        `${process.env.REACT_APP_API_GATEAWAY}/subscribers`,
        { data: { ids: subsToDelete,UI_Description: `${getUIDescription()}\n Delete Subscriber from Report Group`, } }
      );
      if (response.status >= 200 && response.status < 300) {
        toast.success("Subscriber Successfully Deleted from Report Group");
        await fetchSubs();
        setSubsToDelete([]);
      } else {
        toast.error("Failed to delete subscriber from report group");
      }
    } catch (err) {
      console.error("Error deleting subscriber:", err);
      toast.error("Failed to delete subscriber from report group");
    }
  };

  const handleToggle = async (
    subId: string,
    field: keyof Pick<Subscriber, "daily" | "weekly" | "monthly">,
    currentValue: boolean
  ) => {
    try {
      const body = { [field]: !currentValue ,UI_Description: `${getUIDescription()}\n Updating subscriber`,};
      await axios.put(
        `${process.env.REACT_APP_API_GATEAWAY}/subscribers/${subId}`,
        body
      );
      await fetchSubs();
    } catch (err) {
      console.error("Error updating subscriber:", err);
      toast.error("Failed to update subscriber");
    }
  };

  return (
    <div className="flex flex-col mt-2 mx-1">
      {loading ? (
        <div className="w-full min-h-[200px] flex justify-center items-center relative z-10">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-400 border-b-4 border-gray-200"></div>
        </div>
      ) : error ? (
        <div className="w-full text-center text-red-500 font-bold py-2">
          {error}
        </div>
      ) : (
        <>
          {/* Controls */}
          <div className="flex flex-col md:flex-row justify-between mb-4 gap-2">
            <input
              type="text"
              placeholder="Search Subscriber..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="px-3 py-2 rounded w-full md:w-72 bg-gray-800 text-white placeholder-gray-400 focus:outline-none"
            />
            <div className="flex gap-2 justify-end">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                onClick={fetchSubs}
              >
                Refresh
              </button>
              <button
                className="px-4 py-2 border border-blue-600 text-blue-600 rounded hover:bg-blue-600 hover:text-white"
                onClick={() => setAssignSubsDialogOpen(true)}
              >
                Add Subscriber
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-auto max-h-[63vh] border border-gray-700 rounded-lg">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-800 sticky top-0">
                <tr>
                  {[
                    "Name",
                    "Email",
                    "Daily",
                    "Weekly",
                    "Monthly",
                    "Delete",
                  ].map((col) => (
                    <th
                      key={col}
                      className="px-4 py-2 text-left text-sm font-medium text-gray-300 cursor-pointer select-none"
                      onClick={() =>
                        col === "Name" || col === "Email"
                          ? handleRequestSort(
                              col.toLowerCase() as keyof Subscriber["user"]
                            )
                          : undefined
                      }
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredSubsData.length > 0 ? (
                  filteredSubsData.map((sub, idx) => (
                    <tr
                      key={sub.id}
                      className={`${
                        idx % 2 === 0 ? "bg-gray-900" : "bg-gray-800"
                      } hover:bg-gray-700`}
                    >
                      <td className="px-4 py-2 text-sm">{sub.user.name}</td>
                      <td className="px-4 py-2 text-sm">{sub.user.email}</td>
                      <td className="px-4 py-2 text-sm">
                        <input
                          type="checkbox"
                          checked={sub.daily}
                          onChange={() =>
                            handleToggle(sub.id, "daily", sub.daily)
                          }
                          className="w-5 h-5 md:w-6 md:h-6"
                        />
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <input
                          type="checkbox"
                          checked={sub.weekly}
                          onChange={() =>
                            handleToggle(sub.id, "weekly", sub.weekly)
                          }
                          className="w-5 h-5 md:w-6 md:h-6"
                        />
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <input
                          type="checkbox"
                          checked={sub.monthly}
                          onChange={() =>
                            handleToggle(sub.id, "monthly", sub.monthly)
                          }
                          className="w-5 h-5 md:w-6 md:h-6"
                        />
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <button
                          className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                          onClick={() => handleDeleteClick(sub.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center py-4 text-gray-400">
                      No subscribers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Dialogs */}
      <AssignSubsDialog
        open={assignSubsDialogOpen}
        onClose={() => setAssignSubsDialogOpen(false)}
        groupId={group?.id}
        groupName={group?.name}
        onAssigned={fetchSubs}
      />
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}

function AssignSubsDialog({
  open,
  onClose,
  groupId,
  groupName,
  onAssigned,
}: AssignSubsDialogProps) {
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState<Unit[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const API = process.env.REACT_APP_API_GATEAWAY || "";

  // Fetch available units
  useEffect(() => {
    if (!open) return;

    const fetchCandidates = async () => {
      if (!groupId) return setCandidates([]);
      try {
        setLoading(true);
        const res = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/users/unlinked`,
          {
            params: {
              companyId: process.env.REACT_APP_COMPANY_ID,
              type: "subscribers",
              groupId,
            },
          }
        );
        setCandidates(res.data || []);
      } catch (err) {
        console.error("Error fetching subscribers:", err);
        toast.error("Failed to load available subscribers");
      } finally {
        setLoading(false);
      }
    };

    fetchCandidates();
  }, [open, groupId, API]);

  // Toggle selection
  const toggleSelect = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  // Assign selected units
  const handleAssign = async () => {
    if (!groupId || selected.length === 0) {
      toast.warning("Please select at least one subscriber");
      return;
    }

    try {
      const payload = {
        userIds: selected,
        groupId,
        UI_Description: `${getUIDescription()}\n assigned subscribers to report group`,
      };
      await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/subscribers/`,
        payload
      );
      toast.success("Successfully assigned subscribers to report group!");
      setSelected([]);
      setSearch("");
      onClose();
      onAssigned && onAssigned();
    } catch (err) {
      console.error("Error assigning subscribers:", err);
      toast.error("Failed to assign subscribers to report group");
    }
  };

  // Filtered list for search
  const filteredCandidates = candidates.filter((c) => {
    const q = search.toLowerCase();
    return c.name?.toLowerCase().includes(q) || c.id?.toLowerCase().includes(q);
  });

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-40 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 scale-95"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-4 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded bg-slate-800 p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-white"
                >
                  Assign subscribers to {groupName}
                </Dialog.Title>

                {/* Search */}
                <div className="mt-4">
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name or ID..."
                    className="w-full rounded border border-gray-600 bg-slate-700 px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* List */}
                <div className="mt-4">
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500 border-b-4 border-gray-700"></div>
                    </div>
                  ) : filteredCandidates.length === 0 ? (
                    <div className="py-6 text-center text-sm text-gray-300">
                      No matching subscribers
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-auto">
                      {filteredCandidates.map((c) => (
                        <label
                          key={c.id}
                          className={`flex items-center gap-3 p-2 rounded border cursor-pointer hover:bg-slate-600 ${
                            selected.includes(c.id)
                              ? "bg-blue-700 border-blue-400"
                              : "bg-slate-700 border-gray-700"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selected.includes(c.id)}
                            onChange={() => toggleSelect(c.id)}
                            className="w-4 h-4"
                          />
                          <div className="flex-1 text-sm text-white">
                            <div className="font-medium">{c.name}</div>
                            <div className="text-xs text-gray-400">
                              {c.email}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
                    onClick={() => {
                      setSelected([]);
                      setSearch("");
                      onClose();
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    onClick={handleAssign}
                  >
                    Assign ({selected.length})
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

/* ------------------------------ ConfirmDialog ------------------------------ */

function ConfirmDialog({
  open,
  onClose,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-40 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 scale-95"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-4 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded bg-slate-800 p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-white-900"
                >
                  Confirm
                </Dialog.Title>

                <div className="mt-2">
                  <p className="text-sm text-white-500">
                    Are you sure you want to delete the selected subscriber(s)?
                  </p>
                </div>

                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    className="px-4 py-2 bg-gray-900 rounded hover:bg-gray-300"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    onClick={onConfirm}
                  >
                    Delete
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
